/**
 * Create Campaign - Main Stepper Container
 * 
 * 3-Step campaign creation flow:
 * Step 1: Basic Information (name, description, type, validity dates)
 * Step 2: Link Promos & Funding (optional - can be skipped)
 * Step 3: Summary & Preview (review all details, save as draft or activate)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Common components
import Breadcrumbs from '../../../../Common/Breadcrumbs';
import { SuccessModal } from '../../../../Common/SuccessModal';
import './CreateCampaign.css';

// Campaign creation components
import CampaignStepper from './components/CampaignStepper';
import StepBasicInfo from './components/StepBasicInfo';
import StepLinkPromos from './components/StepLinkPromos';
import StepSummary from './components/StepSummary';
import CampaignPreview from './components/CampaignPreview';

// Custom hooks and types
import { useCampaignForm } from './hooks/useCampaignForm';
import { CampaignFormData, CampaignType, CampaignStatus, Campaign } from '../../../../api/promos/types';
import { getCampaignById, updateCampaignStatus, updateCampaign } from '../../../../api/promos';

const CreateCampaign: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  // Check if we're in edit mode
  const isEditMode = location.pathname.includes('/edit');
  const isViewMode = id && !isEditMode;
  
  // Check if we're duplicating an existing campaign
  const duplicateFrom = location.state?.duplicateFrom;
  
  // State for loading campaign data
  const [loadingCampaign, setLoadingCampaign] = useState(isEditMode || isViewMode);
  
  // Store original campaign data for change detection
  const [originalCampaignData, setOriginalCampaignData] = useState<CampaignFormData | null>(null);
  const [originalStatus, setOriginalStatus] = useState<CampaignStatus | null>(null);
  const [campaignName, setCampaignName] = useState<string>('');
  const [hasNavigatedToSummary, setHasNavigatedToSummary] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Initialize form data
  const [initialFormData, setInitialFormData] = useState<CampaignFormData>(() => {
    if (duplicateFrom) {
      return {
        name: `${duplicateFrom.name} (Copy)`,
        description: duplicateFrom.description,
        campaignType: duplicateFrom.campaignType,
        startAt: '', // User must fill dates
        endAt: '', // User must fill dates
        linkedPromos: [], // Reset linked promos
        funding: duplicateFrom.funding,
      };
    }
    return {
      name: '',
      description: '',
      campaignType: CampaignType.SEASONAL,
      startAt: '', // User must fill dates
      endAt: '', // User must fill dates
      linkedPromos: [],
      funding: {
        fundedBy: 'PLATFORM' as const,
      },
    };
  });
  
  // Fetch campaign data if in edit/view mode
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (id && (isEditMode || isViewMode)) {
        try {
          setLoadingCampaign(true);
          const campaign = await getCampaignById(id);
          
          // Format dates for input fields (YYYY-MM-DD)
          const formatDateForInput = (dateString: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            } catch {
              return '';
            }
          };
          
          const formattedData = {
            name: campaign.name,
            description: campaign.description || '',
            campaignType: campaign.campaignType,
            startAt: formatDateForInput(campaign.startAt),
            endAt: formatDateForInput(campaign.endAt),
            linkedPromos: campaign.linkedPromos || [],
            funding: campaign.funding,
          };
          
          setInitialFormData(formattedData);
          // Store original data for change detection
          setOriginalCampaignData(formattedData);
          setOriginalStatus(campaign.status);
          // Store campaign name for header display
          setCampaignName(campaign.name);
        } catch (error: any) {
          const errorMessage = error?.message || 'Failed to load campaign data.';
          setError(errorMessage);
          setTimeout(() => navigate('/promos/campaigns'), 2000);
        } finally {
          setLoadingCampaign(false);
        }
      }
    };
    
    fetchCampaignData();
  }, [id, isEditMode, isViewMode, navigate]);
  
  // Use campaign form hook
  const {
    currentStep,
    formData,
    validationErrors,
    isSubmitting,
    steps,
    goToStep,
    validateStep,
    handleFormChange,
    saveDraft,
    submitCampaign,
    skipStep
  } = useCampaignForm(initialFormData, id, isEditMode);
  
  // Reset navigation flag when campaign ID changes
  useEffect(() => {
    setHasNavigatedToSummary(false);
  }, [id]);
  
  // Navigate to summary page (step 2) when in edit mode and data is loaded
  useEffect(() => {
    if (isEditMode && !loadingCampaign && originalCampaignData && !hasNavigatedToSummary && currentStep === 0) {
      // Navigate directly to summary page (step 2, index 2)
      setHasNavigatedToSummary(true);
      goToStep(2);
    }
  }, [isEditMode, loadingCampaign, originalCampaignData, hasNavigatedToSummary, currentStep, goToStep]);
  
  // Detect if form data has changed (for edit mode)
  const hasChanges = useMemo(() => {
    if (!isEditMode || !originalCampaignData) {
      return false; // New campaign or no original data
    }
    
    // Compare all form fields
    return (
      formData.name !== originalCampaignData.name ||
      formData.description !== originalCampaignData.description ||
      formData.campaignType !== originalCampaignData.campaignType ||
      formData.startAt !== originalCampaignData.startAt ||
      formData.endAt !== originalCampaignData.endAt ||
      JSON.stringify(formData.linkedPromos) !== JSON.stringify(originalCampaignData.linkedPromos) ||
      JSON.stringify(formData.funding) !== JSON.stringify(originalCampaignData.funding)
    );
  }, [formData, originalCampaignData, isEditMode]);
  
  // Handle back navigation
  const handleBack = () => {
    // Check if user came from detail page
    const fromDetail = location.state?.fromDetail;
    
    if (fromDetail && id) {
      // Navigate back to detail page
      navigate(`/promos/campaigns/${id}/detail`);
    } else {
      // Navigate to listing page (backward compatibility)
      navigate('/promos/campaigns');
    }
  };
  
  // Handle step navigation
  const handleStepClick = (stepIndex: number) => {
    // Only allow navigation to previous steps or next step if current is valid
    if (stepIndex < currentStep || validateStep(currentStep)) {
      goToStep(stepIndex);
    }
  };
  
  // Handle next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        goToStep(currentStep + 1);
      }
    }
  };
  
  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };
  
  // Handle skip step 2
  const handleSkipStep = () => {
    if (currentStep === 1) { // Step 2 (Link Promos)
      skipStep(currentStep);
      goToStep(2); // Go to Summary
    }
  };
  
  // Handle save as draft
  const handleSaveDraft = async () => {
    if (!id && !isEditMode) {
      // Create new campaign with DRAFT status
      try {
        await saveDraft();
        setSuccessTitle('Campaign Saved as Draft');
        setSuccessMessage(`Campaign "${formData.name}" saved as draft successfully.`);
        setShowSuccessModal(true);
      } catch (error: any) {
        const errorMessage = error?.message || 'Failed to save campaign as draft. Please try again.';
        setError(errorMessage);
      }
      return;
    }

    // Edit mode: Update content, status stays DRAFT
    if (!id) return;
    
    try {
      // Update content if there are changes
      if (hasChanges) {
        await updateCampaign(id, formData);
      }
      
      // Update status to DRAFT if not already draft
      if (originalStatus !== CampaignStatus.DRAFT) {
        await updateCampaignStatus(id, CampaignStatus.DRAFT);
      }
      
      const action = hasChanges ? 'updated' : 'saved';
      setSuccessTitle('Campaign Saved as Draft');
      setSuccessMessage(`Campaign "${formData.name}" ${action} as draft successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to save campaign as draft. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Handle activate/update campaign
  const handleActivate = async () => {
    if (!id && !isEditMode) {
      // Create new campaign with ACTIVE status
      try {
        await submitCampaign();
        setSuccessTitle('Campaign Activated');
        setSuccessMessage(`Campaign "${formData.name}" created and activated successfully.`);
        setShowSuccessModal(true);
      } catch (error: any) {
        const errorMessage = error?.message || 'Failed to create and activate campaign. Please try again.';
        setError(errorMessage);
      }
      return;
    }

    // Edit mode: Handle content and status updates separately
    if (!id) return;
    
    try {
      // If there are content changes, update content first
      if (hasChanges) {
        await updateCampaign(id, formData);
      }
      
      // Then update status to ACTIVE if needed
      if (originalStatus !== CampaignStatus.ACTIVE) {
        await updateCampaignStatus(id, CampaignStatus.ACTIVE);
      }
      
      const action = hasChanges ? 'updated and activated' : 'activated';
      setSuccessTitle('Campaign Activated');
      setSuccessMessage(`Campaign "${formData.name}" ${action} successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to activate campaign. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Handle pause campaign (when status is ACTIVE)
  const handlePause = async () => {
    if (!id) return;
    
    try {
      await updateCampaignStatus(id, CampaignStatus.PAUSED);
      setSuccessTitle('Campaign Paused');
      setSuccessMessage(`Campaign "${formData.name}" has been paused successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to pause campaign. Please try again.';
      setError(errorMessage);
    }
  };

  // Handle archive campaign (when no changes in edit mode)
  const handleArchive = async () => {
    if (!id) return;
    
    try {
      // Archive by setting status to ARCHIVED
      await updateCampaignStatus(id, CampaignStatus.ARCHIVED);
      setSuccessTitle('Campaign Archived');
      setSuccessMessage(`Campaign "${formData.name}" has been archived successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to archive campaign. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Set page title
  useEffect(() => {
    if (isEditMode) {
      document.title = `Edit Campaign | SFL Admin`;
    } else if (isViewMode) {
      document.title = `View Campaign | SFL Admin`;
    } else if (duplicateFrom) {
      document.title = `Duplicate Campaign - ${duplicateFrom.name} | SFL Admin`;
    } else {
      document.title = 'Create Campaign | SFL Admin';
    }
  }, [isEditMode, isViewMode, duplicateFrom]);
  
  // Show loading state while fetching campaign data
  if (loadingCampaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading campaign data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'Campaigns', onClick: () => navigate('/promos/campaigns') },
              { 
                label: isEditMode 
                  ? 'Edit Campaign' 
                  : isViewMode 
                    ? 'View Campaign'
                    : duplicateFrom 
                      ? `Duplicate - ${duplicateFrom.name}` 
                      : 'Create Campaign', 
                active: true 
              }
            ]} 
          />
        </div>
        
        {/* Header */}
        <div className="campaign-detail-header">
          <div className="campaign-detail-header-content">
            <button
              className="campaign-detail-back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <i className="ph-arrow-left"></i>
            </button>
            <div className="campaign-detail-title-section">
              <h1 className="campaign-detail-title">
                {isEditMode 
                  ? (campaignName || 'Edit Campaign')
                  : isViewMode 
                    ? (campaignName || 'View Campaign')
                    : duplicateFrom 
                      ? `Duplicate Campaign` 
                      : 'Create Campaign'}
              </h1>
              <p className="campaign-detail-subtitle">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
          </div>
        </div>
        
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
            {error}
          </Alert>
        )}
        
        {/* Stepper */}
        <div className="mb-4">
          <CampaignStepper
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
        
        {/* Main Content */}
        <Row>
          {/* Form Section */}
          <Col lg={currentStep === 2 ? 12 : 8}>
            {currentStep === 2 ? (
              /* Step 3 (Summary) - No Card wrapper, transparent background */
              <div className="p-4">
                <StepSummary
                  formData={formData}
                  validationErrors={validationErrors}
                  isSubmitting={isSubmitting}
                  onPrevious={handlePrevious}
                  onSaveDraft={handleSaveDraft}
                  onActivate={handleActivate}
                  onPause={handlePause}
                  onArchive={handleArchive}
                  onEditStep={handleStepClick}
                  isEditMode={isEditMode}
                  hasChanges={hasChanges}
                  originalStatus={originalStatus}
                />
              </div>
            ) : (
              <Card>
                <Card.Body className="p-4">
                  {/* Step Content */}
                  {currentStep === 0 && (
                    <StepBasicInfo
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onCancel={handleBack}
                    />
                  )}
                  
                  {currentStep === 1 && (
                    <StepLinkPromos
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                      onSkip={handleSkipStep}
                    />
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
          
          {/* Preview Section - Hidden on Step 2 (Summary) */}
          {currentStep !== 2 && (
            <Col lg={4}>
              <div className="position-sticky" style={{ top: '100px' }}>
                <CampaignPreview
                  formData={formData}
                  currentStep={currentStep}
                />
              </div>
            </Col>
          )}
        </Row>
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => {
            setShowSuccessModal(false);
            navigate('/promos/campaigns');
          }}
          title={successTitle}
          message={successMessage}
        />
      </Container>
    </div>
  );
};

export default CreateCampaign;